import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, RotateCcw, Trash2, ExternalLink, TrendingDown, AlertTriangle, TrendingUp, Search } from 'lucide-react';
import { useWatchlistContext } from '@/contexts/WatchlistContext';
import type { WatchedDomain } from '@/lib/watchlistStorage';
import SEO from '@/components/SEO';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function AvailabilityPill({ available }: { available: boolean | null }) {
  if (available === true) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Available</span>
  );
  if (available === false) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">Registered</span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Unknown</span>
  );
}

function BrandRiskPill({ state }: { state: WatchedDomain['snapshot']['brandRiskState'] }) {
  const map = {
    clear:   { label: 'Clear',   cls: 'bg-green-100 text-green-700' },
    caution: { label: 'Caution', cls: 'bg-yellow-100 text-yellow-700' },
    risk:    { label: 'Risk',    cls: 'bg-red-100 text-red-600' },
    unknown: { label: '—',       cls: 'bg-gray-100 text-gray-400' },
  };
  const { label, cls } = map[state];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}

const Watchlist: React.FC = () => {
  const navigate = useNavigate();
  const { items, recheckOne, recheckAll, checking, changes, toggle } = useWatchlistContext();
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [recheckingAll, setRecheckingAll] = useState(false);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };
  void showToast; // referenced via handleRemove error path (future use)

  const handleRecheckAll = async () => {
    setRecheckingAll(true);
    recheckAll();
    // Give it a moment then clear the flag
    setTimeout(() => setRecheckingAll(false), items.length * 300 + 2000);
  };

  const handleRemove = (domain: string) => {
    const item = items.find(i => i.domain === domain);
    if (item) toggle(domain, item.snapshot);
  };

  const getChanges = (domain: string) => changes.filter(c => c.domain === domain);

  const sorted = [...items].sort(
    (a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
  );

  return (
    <>
      <SEO
        title="My Watchlist — DomBase"
        description="Domains you're watching. Get instant re-checks on availability, price, and brand risk."
        canonical="/watchlist"
        noindex
      />

      {/* Header */}
      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Star className="h-5 w-5 text-yellow-400 fill-yellow-400 flex-shrink-0" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">My Watchlist</h1>
              <p className="text-sm text-gray-400 mt-0.5">
                {items.length === 0 ? 'No domains saved yet' : `${items.length} domain${items.length !== 1 ? 's' : ''} saved`}
              </p>
            </div>
          </div>
          {items.length > 0 && (
            <button
              onClick={handleRecheckAll}
              disabled={recheckingAll || checking.size > 0}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 bg-white hover:bg-gray-50 rounded-md text-sm font-medium text-gray-600 transition-colors disabled:opacity-50"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${checking.size > 0 ? 'animate-spin' : ''}`} />
              Re-check all
            </button>
          )}
        </div>
      </div>

      {/* Toast */}
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg">
          {toastMsg}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Empty state */}
        {items.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-md p-12 text-center">
            <Star className="h-12 w-12 text-gray-200 mx-auto mb-4" />
            <p className="text-base font-semibold text-gray-700 mb-1">You're not watching any domains yet</p>
            <p className="text-sm text-gray-400 mb-6">Scan a name and tap <strong>★ Watch</strong> to save it here.</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white text-sm font-semibold rounded-md hover:bg-blue-600 transition-colors"
            >
              <Search className="h-4 w-4" />
              Search domains
            </button>
          </div>
        )}

        {/* Watchlist table */}
        {sorted.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Domain</span>
                    </th>
                    <th className="px-4 py-3 text-left w-28">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span>
                    </th>
                    <th className="px-4 py-3 text-right w-28 hidden sm:table-cell">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</span>
                    </th>
                    <th className="px-4 py-3 text-left w-28 hidden md:table-cell">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Brand Risk</span>
                    </th>
                    <th className="px-4 py-3 text-left w-28 hidden lg:table-cell">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Checked</span>
                    </th>
                    <th className="px-4 py-3 w-36" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map(item => {
                    const isChecking = checking.has(item.domain);
                    const itemChanges = getChanges(item.domain);

                    return (
                      <React.Fragment key={item.domain}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          {/* Domain */}
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {item.domain}
                              </span>
                              <button
                                onClick={() => navigate(`/domain/${encodeURIComponent(item.domain)}`)}
                                className="text-gray-400 hover:text-blue-500 transition-colors flex-shrink-0"
                                title="View details"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              Added {timeAgo(item.addedAt)}
                            </div>
                          </td>

                          {/* Availability */}
                          <td className="px-4 py-3.5 w-28">
                            <AvailabilityPill available={item.snapshot.available} />
                          </td>

                          {/* Price */}
                          <td className="px-4 py-3.5 text-right w-28 hidden sm:table-cell">
                            <span className="text-sm font-medium text-gray-700 tabular-nums">
                              {item.snapshot.priceUsd !== null
                                ? `$${item.snapshot.priceUsd.toFixed(2)}`
                                : <span className="text-gray-300">—</span>}
                            </span>
                          </td>

                          {/* Brand risk */}
                          <td className="px-4 py-3.5 w-28 hidden md:table-cell">
                            <BrandRiskPill state={item.snapshot.brandRiskState} />
                          </td>

                          {/* Last checked */}
                          <td className="px-4 py-3.5 text-sm text-gray-400 w-28 hidden lg:table-cell tabular-nums">
                            {timeAgo(item.lastChecked)}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3.5 w-36">
                            <div className="flex items-center gap-2 justify-end">
                              <button
                                onClick={() => recheckOne(item.domain)}
                                disabled={isChecking}
                                title="Re-check now"
                                className="p-1.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-40 transition-colors"
                              >
                                <RotateCcw className={`h-3.5 w-3.5 ${isChecking ? 'animate-spin' : ''}`} />
                              </button>
                              <button
                                onClick={() => handleRemove(item.domain)}
                                title="Remove from watchlist"
                                className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Change indicators */}
                        {itemChanges.length > 0 && (
                          <tr className="bg-amber-50 border-l-4 border-amber-400">
                            <td colSpan={6} className="px-4 py-2">
                              <div className="flex flex-wrap gap-3">
                                {itemChanges.map((ch, i) => (
                                  <span key={i} className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800">
                                    {ch.kind === 'price_drop' && <TrendingDown className="h-3.5 w-3.5 text-green-600" />}
                                    {ch.kind === 'now_unavailable' && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                                    {ch.kind === 'now_available' && <TrendingUp className="h-3.5 w-3.5 text-green-600" />}
                                    {ch.kind === 'price_drop' && `Price dropped ${ch.from} → ${ch.to}`}
                                    {ch.kind === 'now_unavailable' && `Now unavailable (was available)`}
                                    {ch.kind === 'now_available' && `Now available! (was registered)`}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {sorted.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Top 10 most recent domains are re-checked automatically on each visit · Max 50 domains
          </p>
        )}
      </div>
    </>
  );
};

export default Watchlist;
